import React from 'react'
import { FormHelperText, Stack, TextField } from '@mui/material'

export interface ClockDurationFieldsChange {
  minutes: number
  seconds: number
}

export interface ClockDurationFieldsProps {
  minutes: number
  seconds: number
  onChange: (value: ClockDurationFieldsChange) => void
  helperText?: React.ReactNode
  error?: boolean
}

const clampMinutes = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0
  }
  return Math.max(0, Math.floor(value))
}

const normalizeComponents = (minutes: number, seconds: number): ClockDurationFieldsChange => {
  let nextMinutes = clampMinutes(minutes)

  let nextSeconds = 0
  if (Number.isFinite(seconds)) {
    nextSeconds = Math.floor(seconds)
  }

  if (nextSeconds >= 60) {
    const extraMinutes = Math.floor(nextSeconds / 60)
    nextMinutes += extraMinutes
    nextSeconds -= extraMinutes * 60
  }

  if (nextSeconds < 0) {
    const deficitMinutes = Math.ceil(Math.abs(nextSeconds) / 60)
    nextMinutes = Math.max(0, nextMinutes - deficitMinutes)
    nextSeconds += deficitMinutes * 60
  }

  nextSeconds = Math.max(0, Math.min(59, nextSeconds))

  return { minutes: nextMinutes, seconds: nextSeconds }
}

const ClockDurationFields: React.FC<ClockDurationFieldsProps> = ({
  minutes,
  seconds,
  onChange,
  helperText,
  error = false,
}) => {
  const handleMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawMinutes = Number(event.target.value)
    const normalized = normalizeComponents(rawMinutes, seconds)
    onChange(normalized)
  }

  const handleSecondsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawSeconds = Number(event.target.value)
    const normalized = normalizeComponents(minutes, rawSeconds)
    onChange(normalized)
  }

  return (
    <Stack spacing={0.75} sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        sx={{ flexWrap: 'wrap' }}
      >
        <TextField
          type="number"
          label="Minutes"
          value={minutes}
          onChange={handleMinutesChange}
          fullWidth
          inputProps={{ min: 0, max: 59, step: 1 }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TextField
          type="number"
          label="Seconds"
          value={seconds}
          onChange={handleSecondsChange}
          fullWidth
          inputProps={{ min: 0, max: 59, step: 10 }}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Stack>
      {helperText ? <FormHelperText error={error}>{helperText}</FormHelperText> : null}
    </Stack>
  )
}

export default ClockDurationFields
